import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";

export const validFee = (v) => /^\d{1,2}(\.\d{1,2})?$/.test(String(v)) && Number(v) >= 0.5 && Number(v) <= 50;
export const validWeeks = (v) => /^\d{1,2}$/.test(String(v)) && Number(v) >= 1 && Number(v) <= 52;

// Standard / Reduced percentages from the Price List (fallback 10 / 5 until loaded).
let cached = null;
export const resetFeeOptions = () => { cached = null; };
export function useFeeOptions() {
  const [o, setO] = useState({ standard: "10", reduced: "5" });
  useEffect(() => {
    let live = true;
    (cached ||= api.get("/contract-fee-options").then((r) => r.data).catch(() => null)).then((d) => { if (live && d) setO(d); });
    return () => { live = false; };
  }, []);
  return o;
}

// Moves an untouched "10" starting value to the Price List Standard once loaded.
export function useStandardDefault(value, setValue, opts) {
  const touched = useRef(false);
  useEffect(() => { if (!touched.current && value === "10" && opts.standard !== "10") setValue(opts.standard); }, [opts.standard]); // eslint-disable-line react-hooks/exhaustive-deps
  return () => { touched.current = true; };
}

export function AdvertisingTermsPicker({ fee, onChange, autoDefault }) {
  const opts = useFeeOptions();
  const touch = useStandardDefault(autoDefault ? fee : null, (v) => onChange({ fee: v }), opts);
  const set = (v) => { touch(); onChange({ fee: v }); };
  const presets = [opts.standard, opts.reduced];
  const preset = presets.includes(fee);
  const btn = (v, k) => (
    <button key={k} type="button" data-testid={`fee-quick-${k}`} aria-pressed={fee === v} onClick={() => set(v)}
      className={`h-11 rounded-full border px-4 text-sm font-semibold ${fee === v ? "bg-amber-800 border-amber-800 text-white" : "bg-white border-stone-300 text-stone-700"}`}>{v}%</button>
  );
  return (
    <div data-testid="advertising-terms-picker" className="space-y-3">
      <div>
        <div id="fee-label" className="text-sm font-semibold text-stone-800 mb-1.5">Advertising Fee (clause 2(b))</div>
        <div className="flex flex-wrap items-center gap-2" role="group" aria-labelledby="fee-label">
          {btn(opts.standard, "standard")}{btn(opts.reduced, "reduced")}
          <div className="relative w-32">
            <Input data-testid="fee-custom" inputMode="decimal" aria-label="Custom Advertising Fee percent" placeholder="Custom"
              value={preset ? "" : fee} onChange={(e) => set(e.target.value.replace(/[^\d.]/g, ""))}
              className={`h-11 pr-7 ${!preset && fee ? "border-amber-800" : ""}`} />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-500">%</span>
          </div>
        </div>
        {!validFee(fee) && <p data-testid="fee-error" className="mt-1 text-xs text-red-600">Enter 0.5% to 50% (up to 2 decimals).</p>}
      </div>
      <div data-testid="terms-summary" className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-900">
        Advertising Fee: <strong>{validFee(fee) ? `${Number(fee)}%` : "—"}</strong>
        <div className="text-xs text-amber-800 mt-0.5">The seller chooses the number of advertising weeks on their agreement.</div>
      </div>
    </div>
  );
}
